// Store products by what you type (a name, a pid), with their pictures; each
// result toggles in the selection, and "Add" hands the chosen ones back. The
// products already in the group are marked and cannot be picked again.
// The composition tab picks its parts with it too (`title`, `excludedLabel`).
import React, { useEffect, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon, CheckCircleIcon } from "@heroicons/react/24/outline"
import { url } from "aldoo-ra/CMS/url"
import { searchProducts } from "./api"
import { textOf } from "./values"

export default function ProductPicker({ isOpen, onClose, onPick, exclude = [], title = "Add products to the group", excludedLabel = "in this group" }) {
  const [text, setText] = useState("")
  const [results, setResults] = useState([])
  const [busy, setBusy] = useState(false)
  const [chosen, setChosen] = useState([])
  const run = useRef(0)

  useEffect(() => {
    if (!isOpen) return
    setChosen([])
  }, [isOpen])

  // a search a moment after the typing stops; the last one wins
  useEffect(() => {
    const term = text.trim()
    if (term.length < 2) return setResults([])
    const id = ++run.current
    setBusy(true)
    const timer = setTimeout(async () => {
      const found = await searchProducts(term).catch(() => [])
      if (id !== run.current) return
      setResults(found)
      setBusy(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [text])

  const toggle = (product) => {
    if (exclude.includes(product.pid)) return
    setChosen((list) => (list.includes(product.pid) ? list.filter((pid) => pid !== product.pid) : [...list, product.pid]))
  }

  const add = () => {
    onPick(chosen)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="admin_centered" title={title} closeOnEscape className="w-full md:w-[860px]">
      <div className="flex flex-col gap-4" data-testid="variant-group-picker">
        <Input
          variant="admin"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search products by name or pid…"
          className="w-full"
          autoFocus
          icon={<MagnifyingGlassIcon className="h-5 w-5 text-admin_text_secondary" />}
          showClearButton={text.length > 0}
          onClear={() => setText("")}
        />

        <div className="min-h-[260px] max-h-[55vh] overflow-y-auto">
          {text.trim().length < 2 && <p className="m-0 text-sm text-admin_text_secondary">Type at least two letters.</p>}
          {busy && <p className="m-0 text-sm text-admin_text_secondary">Searching…</p>}
          {!busy && text.trim().length >= 2 && !results.length && <p className="m-0 text-sm text-admin_text_secondary">Nothing found.</p>}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {results.map((product) => {
              const on = chosen.includes(product.pid)
              const already = exclude.includes(product.pid)
              return (
                <button
                  key={product.pid}
                  type="button"
                  onClick={() => toggle(product)}
                  disabled={already}
                  className={`relative text-left rounded-lg border p-2 transition-colors ${on ? "border-admin_primary bg-admin_primary/5" : "border-admin_bordered hover:border-admin_primary"} ${already ? "opacity-50 cursor-default" : ""}`}
                  data-testid="variant-group-picker-result"
                >
                  <div className="aspect-square rounded overflow-hidden bg-admin_neutral_1 mb-2">
                    {product.thumbnail && <img src={url(product.thumbnail)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="text-xs font-semibold text-admin_text line-clamp-2">{textOf(product.name)}</div>
                  <div className="text-[11px] text-admin_text_secondary line-clamp-1">{product.pid}</div>
                  {already && <div className="text-[11px] text-admin_text_secondary">{excludedLabel}</div>}
                  {on && <CheckCircleIcon className="absolute top-3 right-3 w-6 h-6 text-admin_primary" />}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-admin_text_secondary">{chosen.length ? `${chosen.length} selected` : "Pick one or more"}</span>
          <Button variant="admin_primary" disabled={!chosen.length} onClick={add} data-testid="variant-group-picker-add">
            {chosen.length > 1 ? `Add ${chosen.length} products` : "Add product"}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
